import { checkSessionLost, CSRF_FAILED_HEADER, fetchSession } from './session';

/**
 * Get CSRF token from meta tag
 * This reads the token that Yii2 keeps updated in the HTML head
 */
function getCsrfToken(): string | null {
  // Only access DOM when this function is called, not at module load time
  if (typeof document === 'undefined') {
    return null;
  }
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  return metaTag ? metaTag.getAttribute('content') : null;
}

/**
 * Replace the CSRF token in the meta tag.
 * Yii regenerates the token on login/signup, so auth responses include the fresh
 * token and must call this — otherwise every subsequent POST fails validation
 * until a full page load.
 */
export function updateCsrfMetaToken(token: string | undefined | null): void {
  if (!token || typeof document === 'undefined') {
    return;
  }
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  if (metaTag) {
    metaTag.setAttribute('content', token);
  }
}

const STATE_CHANGING = ['POST', 'PUT', 'DELETE', 'PATCH'];

/** Whether a request body can be sent a second time (not a one-shot stream). */
const canResend = (body: RequestInit['body']): boolean =>
  !(typeof ReadableStream !== 'undefined' && body instanceof ReadableStream);

/**
 * Wrapper around native fetch that automatically includes CSRF token.
 *
 * Asks for JSON (`Accept`), so a login-required endpoint answers a guest with
 * a 403 marked `X-Login-Required` instead of redirecting to the login form
 * (yii\web\User::loginRequired() decides by the Accept header, not by
 * X-Requested-With, which would turn every redirect into an `X-Redirect`
 * without a Location). A member's lost session is handled in `session.ts`.
 * A refused CSRF token (400 marked `X-CSRF-Failed`) is refreshed once and the
 * request retried; when that fails too, the page is reloaded.
 */
export async function fetchWithCsrf(url: string, options: RequestInit = {}, retried = false): Promise<Response> {
  const csrfToken = getCsrfToken();
  const method = options.method?.toUpperCase() || '';
  
  // Clone headers to avoid mutating the original
  const headers = new Headers(options.headers || {});
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json, text/plain, */*');
  }
  
  // Add CSRF token for state-changing methods
  if (csrfToken && STATE_CHANGING.includes(method)) {
    headers.set('X-CSRF-Token', csrfToken);
  }
  
  const response = await fetch(url, {
    ...options,
    headers
  });

  if (await checkSessionLost(response)) {
    return response;
  }

  // CSRF token refused (the session's token changed): fetch a fresh one and retry once
  if (response.status === 400 && STATE_CHANGING.includes(method) && response.headers.get(CSRF_FAILED_HEADER) !== null) {
    if (!retried && canResend(options.body)) {
      const session = await fetchSession();
      if (session?.csrfToken) {
        updateCsrfMetaToken(session.csrfToken);
        return fetchWithCsrf(url, options, true);
      }
    }
    console.error('CSRF token validation failed. Reloading page...');
    window.location.reload();
  }

  return response;
}

/**
 * Convenience methods for common HTTP operations
 */
export const api = {
  get: (url: string, options?: RequestInit) => 
    fetchWithCsrf(url, { ...options, method: 'GET' }),
    
  post: (url: string, body?: any, options?: RequestInit) => 
    fetchWithCsrf(url, {
      ...options,
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
      headers: {
        ...options?.headers,
        ...(!(body instanceof FormData) && { 'Content-Type': 'application/json' })
      }
    }),
    
  put: (url: string, body?: any, options?: RequestInit) => 
    fetchWithCsrf(url, {
      ...options,
      method: 'PUT',
      body: body instanceof FormData ? body : JSON.stringify(body),
      headers: {
        ...options?.headers,
        ...(!(body instanceof FormData) && { 'Content-Type': 'application/json' })
      }
    }),
    
  delete: (url: string, options?: RequestInit) => 
    fetchWithCsrf(url, { ...options, method: 'DELETE' })
};

/**
 * Legacy exports for backward compatibility
 * @deprecated Use named exports instead
 */
export default fetchWithCsrf;